import React, { useEffect, useMemo, useState } from 'react';
import { adminAPI } from '../config/apiService';

// SIP sites (same list/rationale as the qual managers).
const SIP_SITES = [
    { siteID: 5, site: 'CW Valencia 1', code: 'VAL01' },
    { siteID: 6, site: 'CW Valencia 2', code: 'VAL02' },
    { siteID: 3, site: 'CW Sacramento', code: 'SAC05' },
];

// Drawing tab — the eligible pool for a site/month, built from the saved
// Employee Qualification Maintenance (all 5 criteria + not a recordable month).
// One line per eligible employee with their ID, ready to feed the draw.
function DrawingManager() {
    const now = new Date();
    const access = (localStorage.getItem('admin_access_visibility') || 'ALL-ALL').toUpperCase();
    const myCode = access.split('-')[1] || 'ALL';
    const visibleSites = myCode === 'ALL' ? SIP_SITES : SIP_SITES.filter((s) => s.code === myCode);

    const [siteID, setSiteID] = useState(visibleSites[0]?.siteID || SIP_SITES[0].siteID);
    // Default to LAST month — the reporting period being drawn this month.
    const _lm = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const [yyyymm, setYyyymm] = useState(`${_lm.getFullYear()}-${String(_lm.getMonth() + 1).padStart(2, '0')}`);
    const [year, month] = useMemo(() => yyyymm.split('-').map(Number), [yyyymm]);

    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [msg, setMsg] = useState('');

    const reload = async () => {
        setLoading(true); setMsg('');
        try {
            const res = await adminAPI.getDrawingList(siteID, year, month);
            setRows(Array.isArray(res.data) ? res.data : []);
        } catch (err) {
            setMsg(`Failed to load: ${err?.response?.data || err?.message || 'unknown error'}`);
            setRows([]);
        } finally { setLoading(false); }
    };

    useEffect(() => { reload(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [siteID, yyyymm]);

    const siteName = SIP_SITES.find((s) => s.siteID === siteID)?.site || '';
    const monthLabel = new Date(year, month - 1, 1).toLocaleString('en-US', { month: 'long', year: 'numeric' });

    // Copy the eligible list (one "ID  Name" per line) for pasting into the draw.
    const copyList = () => {
        const text = rows.map((r) => `${r.employeeID}\t${r.firstName} ${r.lastName}`).join('\n');
        if (navigator.clipboard) navigator.clipboard.writeText(text).then(
            () => setMsg(`Copied ${rows.length} name${rows.length === 1 ? '' : 's'}.`),
            () => setMsg('Copy failed — select the list manually.')
        );
    };

    return (
        <div>
            <div style={styles.card}>
                <div style={styles.eyebrow}>Drawing</div>
                <div style={styles.title}>Eligible pool — {monthLabel}</div>
                <div style={styles.muted}>
                    The eligible employees for {siteName}, built from saved Employee Qualification Maintenance.
                    A recordable month, or unfilled quals, yields no eligible names.
                </div>
                <div style={styles.bulkRow}>
                    <label style={styles.bulkLabel}>Site:</label>
                    <select value={siteID} onChange={(e) => setSiteID(Number(e.target.value))} style={styles.input} disabled={visibleSites.length === 1}>
                        {visibleSites.map((s) => <option key={s.siteID} value={s.siteID}>{s.site}</option>)}
                    </select>
                    <label style={styles.bulkLabel}>Month:</label>
                    <input type="month" value={yyyymm} onChange={(e) => setYyyymm(e.target.value)} style={styles.input} />
                    <span style={{ ...styles.muted, marginLeft: 'auto' }}>{rows.length} eligible</span>
                    <button onClick={copyList} disabled={rows.length === 0} style={btn('#2563eb', rows.length === 0)}>Copy list</button>
                </div>
            </div>

            {msg && <Banner>{msg}</Banner>}

            <div style={{ ...styles.card, marginTop: 12 }}>
                {loading ? (
                    <div style={styles.muted}>Loading…</div>
                ) : rows.length === 0 ? (
                    <div style={styles.muted}>No eligible employees for {siteName} in {monthLabel}.</div>
                ) : (
                    <table style={styles.table}>
                        <thead style={styles.thead}>
                            <tr>
                                <th style={{ ...styles.th, width: 90 }}>ID</th>
                                <th style={styles.th}>Name</th>
                                <th style={styles.th}>Department</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.employeeID} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                    <td style={{ ...styles.td, color: '#6b7280' }}>{r.employeeID}</td>
                                    <td style={{ ...styles.td, fontWeight: 600 }}>{r.lastName}, {r.firstName}</td>
                                    <td style={{ ...styles.td, color: '#6b7280' }}>{r.department}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

function Banner({ children }) {
    return <div style={{ margin: '12px 0 0', padding: '8px 12px', borderRadius: 4, background: '#f3f4f6', color: '#374151', fontSize: 13 }}>{children}</div>;
}

const btn = (bg, disabled) => ({
    background: bg, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 4,
    fontWeight: 600, fontSize: 13, cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1
});

const styles = {
    card: { background: 'white', padding: 16, borderRadius: 8, border: '1px solid #e5e7eb' },
    eyebrow: { fontSize: 10, fontWeight: 700, color: '#9ca3af', letterSpacing: '0.1em', textTransform: 'uppercase' },
    title: { fontSize: 20, fontWeight: 800, color: '#111827', marginTop: 2 },
    muted: { color: '#6b7280', fontSize: 13 },
    bulkRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 10 },
    bulkLabel: { fontSize: 12, fontWeight: 700, color: '#374151' },
    input: { padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 },
    table: { width: '100%', borderCollapse: 'collapse' },
    thead: { background: '#f9fafb', borderBottom: '1px solid #e5e7eb' },
    th: { textAlign: 'left', padding: '8px 12px', fontSize: 11, fontWeight: 700, color: '#374151', textTransform: 'uppercase', letterSpacing: '0.04em' },
    td: { padding: '8px 12px', fontSize: 14, color: '#111827' }
};

export default DrawingManager;
